import { PortalPartnerAccount } from 'vopay';

/**
 * The extra account fields the Parent Account picker asks `/portal/partner/accounts` for (ID-13797).
 * Passing `Fields` is also what widens the response to the partner's whole book — inactive accounts,
 * nested sub-accounts and its Partner Accounts' accounts — so every eligible parent is listed whatever
 * the Accounts list is filtered or paged to.
 */
export const PARENT_ACCOUNT_FIELDS =
  'Status,SubAccountsEnabled,Country,Currency,BillingPackageName,ParentAccountID,ParentAccountName,PartnerAccountID,PartnerAccountName';

export type ParentAccountOption = {
  accountID: string;
  accountName: string;
  /** Ancestor names top-down, ending with the account's own — e.g. Harbourview Rentals › Harbourview East. */
  path: string[];
  /** The owning Partner Account's name when it is not the logged-in partner itself; empty otherwise. */
  partnerAccountName: string;
  /** Alpha-2 country abbreviation, as the endpoint returns it. */
  country: string;
  currency: string;
  billingPackageName: string;
};

/** Spec 5.1: an account can hold sub-accounts when it is active and the entitlement is on. */
export function isEligibleParent(account: PortalPartnerAccount): boolean {
  return account.Status === 'active' && account.SubAccountsEnabled === '1';
}

// Walks ParentAccountID up through the loaded list. An ancestor the list does not carry (which the
// endpoint's scoping should never produce) ends the walk on the name the row itself knows.
function accountPath(account: PortalPartnerAccount, byID: Map<string, PortalPartnerAccount>): string[] {
  const names = [account.AccountName];
  const seen = new Set([account.APIAccountID]);
  let current = account;

  while (current.ParentAccountID && !seen.has(current.ParentAccountID)) {
    seen.add(current.ParentAccountID);

    const parent = byID.get(current.ParentAccountID);

    if (!parent) {
      if (current.ParentAccountName) names.unshift(current.ParentAccountName);

      break;
    }

    names.unshift(parent.AccountName);
    current = parent;
  }

  return names;
}

const PATH_SEPARATOR = ' › ';

/**
 * The eligible parents out of the loaded book, ordered by their path so a sub-account follows the
 * account it sits under. `ownPartnerAccountID` is the logged-in partner's id: accounts owned by one
 * of its Partner Accounts carry that partner's name as a tag, matching the Accounts list's Partner
 * Account Name column.
 */
export function toParentAccountOptions(
  accounts: PortalPartnerAccount[],
  ownPartnerAccountID: string,
): ParentAccountOption[] {
  const byID = new Map(accounts.map((account) => [account.APIAccountID, account]));

  return accounts
    .filter(isEligibleParent)
    .map((account) => ({
      accountID: account.APIAccountID,
      accountName: account.AccountName,
      path: accountPath(account, byID),
      partnerAccountName:
        account.PartnerAccountID && account.PartnerAccountID !== ownPartnerAccountID
          ? (account.PartnerAccountName ?? '')
          : '',
      country: account.Country ?? '',
      currency: account.Currency ?? '',
      billingPackageName: account.BillingPackageName ?? '',
    }))
    .sort((a, b) => a.path.join(PATH_SEPARATOR).localeCompare(b.path.join(PATH_SEPARATOR)));
}

/** The picker's option text and the Review's value: "A › B · b_id", tagged "(Partner)" when another partner owns it. */
export function formatParentAccountLabel({ path, accountID, partnerAccountName }: ParentAccountOption): string {
  const label = `${path.join(PATH_SEPARATOR)} · ${accountID}`;

  return partnerAccountName ? `${label} (${partnerAccountName})` : label;
}
